import { Box, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { Form, useCheckAuth, useSafeSetState, useTranslate } from 'ra-core'
import { useEffect, useState } from 'react'
import { FormDataConsumer, PasswordInput, required, useDataProvider, useLogin, useNotify } from 'react-admin'
import { useLocation, useNavigate } from 'react-router-dom'

import { useDocumentTitle } from '../../App'
import AuthFormSection from '../../components/login/AuthFormSection'
import EmailInput from '../../components/login/EmailInput'
import FormCard from '../../components/login/FormCard'
import ResetPage from '../../components/login/ResetPage'
import SSOButton, { ssoProviders } from '../../components/login/SSOButton'

const useAvailableLoginMethods = (email) => {
  const dataProvider = useDataProvider()

  const { data } = useQuery({
    queryKey: ['availableLoginMethods', email],
    queryFn: () => dataProvider.getAvailableLoginMethods(email),
    enabled: !!email,
  })

  return data?.map((method) => method.toLowerCase().replace('_com', '')) || []
}

const useLoginWithRedirection = (loginRedirectionPathName) => {
  const login = useLogin()
  const notify = useNotify()

  return ({ values, loginType }) =>
    login({ ...values, loginType })
      .then(() => {
        if (loginRedirectionPathName !== '/login') window.location.replace(loginRedirectionPathName)
      })
      .catch((error) => {
        notify(typeof error === 'string' ? error : error?.message || 'ra.auth.sign_in_error', {
          type: 'error',
          messageArgs: { _: typeof error === 'string' ? error : error && error.message ? error.message : undefined },
        })
      })
}

export function useAutomaticLogin({ availableLoginMethods, login, username }) {
  const onlyProvider = availableLoginMethods.length === 1 ? availableLoginMethods[0] : undefined
  const shouldConnectWithSSO = ssoProviders.some(({ key }) => key === onlyProvider)

  useEffect(() => {
    if (shouldConnectWithSSO) login({ loginType: onlyProvider, values: { username } })
  }, [shouldConnectWithSSO]) // eslint-disable-line react-hooks/exhaustive-deps

  return shouldConnectWithSSO
}

const LoginForm = ({ login, email, availableLoginMethods }) => {
  const [submittingProvider, setSubmittingProvider] = useSafeSetState('')
  const translate = useTranslate()
  const submit = (values, e, loginType = 'password') => {
    setSubmittingProvider(loginType)
    const safeValues = e ? { username: e.target[0].value, password: e.target[1].value } : values
    login({ values: safeValues, loginType }).finally(() => setSubmittingProvider(''))
  }

  const disabled = !!submittingProvider

  return (
    <Form onSubmit={submit} noValidate disableInvalidFormNotification>
      {availableLoginMethods.includes('password') && (
        <FormDataConsumer>
          {({ formData }) => (
            <AuthFormSection
              isLoading={submittingProvider === 'password'}
              disabled={disabled}
              submitText="ra.auth.sign_in"
              linkTo={ResetPage.path + '?email=' + encodeURIComponent(formData.username || email)}
              linkText={translate('mymove.login.forgotPassword')}
            >
              <EmailInput defaultValue={email} />
              <PasswordInput
                source="password"
                label={translate('ra.auth.password')}
                slotProps={{ htmlInput: { autoComplete: 'current-password' } }}
                sx={{ width: '100%', margin: '0' }}
                validate={required()}
                variant="standard"
              />
            </AuthFormSection>
          )}
        </FormDataConsumer>
      )}
      {ssoProviders
        .filter(({ key }) => availableLoginMethods.includes(key))
        .map(({ key: provider }) => (
          <SSOButton
            key={provider}
            provider={provider}
            disabled={disabled}
            onClick={() => submit({}, null, provider)}
            startIcon={submittingProvider === provider ? <CircularProgress size={18} thickness={2} /> : undefined}
          />
        ))}
    </Form>
  )
}

export const LoginPage = () => {
  const checkAuth = useCheckAuth()
  const location = useLocation()
  const navigate = useNavigate()
  useDocumentTitle('Sign in')

  const params = new URLSearchParams(location.search)
  const initialEmail = params.get('email')

  const [email, setEmail] = useState(initialEmail)
  const [loginRedirectionPathName, setLoginRedirectionPathName] = useState('/')

  useEffect(() => {
    checkAuth({}, false)
      .then(() => {
        // already authenticated, redirect to the home page
        navigate('/')
      })
      .catch(() => {
        // not authenticated, stay on the login page & store pathname for redirection when logging in
        if (location.state?.nextPathname) {
          // we remove "/login" from the redirection path if applicable to avoid redirection issues
          const loginPathRegex = new RegExp(LoginPage.path)
          setLoginRedirectionPathName(location.state.nextPathname.replace(loginPathRegex, ''))
        }
      })
  }, [checkAuth, navigate, location.state?.nextPathname, setLoginRedirectionPathName])

  const login = useLoginWithRedirection(loginRedirectionPathName)
  const availableLoginMethods = useAvailableLoginMethods(email)
  const willAutomaticallyLogin = useAutomaticLogin({ availableLoginMethods, login, username: email })

  return (
    <FormCard>
      <Box sx={{ pb: '8px' }}>
        {!willAutomaticallyLogin ? (
          email ? (
            <LoginForm login={login} email={email} availableLoginMethods={availableLoginMethods} />
          ) : (
            <Form onSubmit={(values) => setEmail(values.username)} noValidate disableInvalidFormNotification>
              <AuthFormSection isLoading={email} disabled={email} submitText="Next">
                <EmailInput />
              </AuthFormSection>
            </Form>
          )
        ) : null}
      </Box>
    </FormCard>
  )
}

LoginPage.path = '/login'
